'use client';

import { CredentialType } from '#/components/ethereum/credential-type';
import {
  useIsWalletMatch,
  WalletInput,
} from '#/components/ethereum/wallet-input';
import { Button } from '#/components/ui/button';
import {
  DialogClose,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '#/components/ui/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '#/components/ui/form';
import { Input } from '#/components/ui/input';
import { Label } from '#/components/ui/label';
import { useIsCurrentEpochPastTarget } from '#/data/v2/useEthereumCurrentEpoch';
import {
  TransactionDetails,
  TransactionViewOnButton,
} from '#/modules/display/transaction';
import { ValidatorIndex } from '#/modules/display/validator-index';
import { ValidatorStatus } from '#/modules/display/validator-status';
import { zodResolver } from '@hookform/resolvers/zod';
import { useActor } from '@xstate/react';
import { Translation } from 'intl-lib';
import { Loader2 } from 'lucide-react';
import { useContext, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { formatEther, parseEther } from 'viem';
import * as z from 'zod';
import { MachineContext } from './contexts/machine';

export function Information() {
  const interpreter = useContext(MachineContext);
  const [state, dispatch] = useActor(interpreter);
  const target = state.context.originalRow;
  const amount = state.context.amount;
  const transactionHash = state.context.transactionHash;

  const isPartialWithdrawalable = useIsCurrentEpochPastTarget(
    target.activeEpoch,
    256
  );

  const isWalletMatch = useIsWalletMatch(target.withdrawaddr);

  const availableBalance = useMemo(
    () => parseEther(target.effectiveBalance) - parseEther('32'),
    [target]
  );

  const formSchema = useMemo(
    () =>
      z.object({
        amount: z.string().superRefine((val, ctx) => {
          let ether: bigint | null = null;
          try {
            ether = parseEther(val);
          } catch {}
          if (ether != null) {
            if (ether > availableBalance) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message:
                  'The remaining balance after withdrawal must not be less than 32 ETH.',
              });
            }
          } else {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Please enter a valid ETH amount.',
            });
          }
        }),
      }),
    [availableBalance]
  );
  // 1. Define your form.
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      amount: amount != null ? formatEther(amount) : '0',
    },
  });

  // 2. Define a submit handler.
  async function onSubmit(values: z.infer<typeof formSchema>) {
    // Do something with the form values.
    // ✅ This will be type-safe and validated.
    console.log('onSubmit', values);
    const { amount } = values;
    dispatch({
      type: 'NEXT',
      data: {
        amount: parseEther(amount),
      },
    });
  }

  const isLoading = state.hasTag('loading');

  return (
    <>
      <DialogHeader>
        <DialogTitle>
          <Translation path="my_staking.partial_withdrawal_dialog.title" />
        </DialogTitle>
      </DialogHeader>
      <div className="flex flex-col gap-2">
        <Label>
          <Translation path="EthereumStaking.WithdrawalAddress" />
        </Label>
        <div>
          <WalletInput address={target.withdrawaddr} />
        </div>
      </div>
      <div className="flex flex-col gap-2">
        <Label>Selected Validator</Label>
        <div className="border rounded-xl p-3 flex flex-col gap-1">
          <div className="grid grid-cols-4 items-center gap-2 text-xs text-muted-foreground">
            <div>
              <Translation path="my_staking.order.validator_column.validator" />
            </div>
            <div className="truncate">
              <Translation path="my_staking.order.validator_column.credential_type" />
            </div>
            <div>
              <Translation path="my_staking.order.validator_column.balance" />
            </div>
            <div>
              <Translation path="my_staking.order.validator_column.status" />
            </div>
          </div>
          <div className="h-9 grid grid-cols-4 items-center gap-2 text-sm font-medium">
            <div>
              <ValidatorIndex
                validatorIndex={target.validatorIndex}
                publicKey={target.pubKey}
              />
            </div>
            <div>
              <CredentialType credentialType={target.credentialType} />
            </div>
            <div>{target.effectiveBalance} ETH</div>
            <div>
              <ValidatorStatus item={target} />
            </div>
          </div>
        </div>
      </div>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <FormField
            control={form.control}
            name="amount"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Amount</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    type="number"
                    max={formatEther(availableBalance)}
                    min={0}
                  />
                </FormControl>
                <FormDescription>
                  Available for withdrawal: {formatEther(availableBalance)} ETH
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          {/* <dl className="flex items-center justify-between rounded-lg px-5 py-4  bg-linear-to-r from-[#E9F0FF] via-white to-[#F9F5FF]">
            <dt>Balance after partial withdrawal</dt>
            <dd>
              <BalanceAfterWithdrawal /> ETH
            </dd>
          </dl> */}
          {transactionHash != null && (
            <div className="flex flex-col gap-2">
              <Label>Transaction</Label>
              <div>
                <TransactionDetails transactionHash={transactionHash} />
              </div>
            </div>
          )}
        </form>
      </Form>
      <DialogFooter className="gap-2 sm:space-x-0">
        <DialogClose asChild>
          <Button variant="secondary" type="button">
            <Translation path="Application.Dialog.CancelButtonLabel" />
          </Button>
        </DialogClose>
        {transactionHash != null ? (
          <TransactionViewOnButton transactionHash={transactionHash} />
        ) : (
          <Button
            type="button"
            variant="primary"
            disabled={
              !isPartialWithdrawalable ||
              !isWalletMatch ||
              form.formState.isSubmitting
            }
            onClick={form.handleSubmit(onSubmit)}
          >
            {isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
            {/* <Translation path="Application.Dialog.SubmitButtonLabel" /> */}
            Partial withdrawal
          </Button>
        )}
      </DialogFooter>
    </>
  );
}
